import React, { useState } from "react";
import { User as UserIcon, KeyRound, Save, Check } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/apiClient";
import { useAuth } from "../lib/AuthContext";

export default function ProfilePage() {
  const { user } = useAuth();
  const [current, setCurrent] = useState("");
  const [next1, setNext1] = useState("");
  const [next2, setNext2] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setOk(false);
    if (next1.length < 6) { setErr("New password must be at least 6 characters."); return; }
    if (next1 !== next2) { setErr("New passwords do not match."); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next1 });
      setOk(true);
      setCurrent(""); setNext1(""); setNext2("");
    } catch (ex) {
      setErr(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6 fade-up max-w-2xl" data-testid="profile-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1.5 font-semibold">My account</div>
        <h1 className="qb-section-title text-3xl sm:text-4xl flex items-center gap-3">
          <UserIcon size={28} className="text-brand-blue" /> Profile
        </h1>
        <p className="text-sm text-slate-500 mt-1">Account details and password.</p>
      </div>

      <div className="qb-card p-5">
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">Account</div>
        <dl className="grid grid-cols-2 gap-y-2 text-sm">
          <dt className="text-slate-500">Name</dt><dd className="font-medium" data-testid="profile-name">{user?.name || "—"}</dd>
          <dt className="text-slate-500">Email</dt><dd className="font-mono text-xs" data-testid="profile-email">{user?.email}</dd>
          <dt className="text-slate-500">Role</dt><dd className="capitalize">{user?.role}</dd>
          <dt className="text-slate-500">Branch</dt><dd>{user?.branch || "—"}</dd>
        </dl>
      </div>

      <form onSubmit={submit} className="qb-card p-5 space-y-4" data-testid="change-password-form">
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold inline-flex items-center gap-2">
          <KeyRound size={14} className="text-brand-blue" /> Change password
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-600 mb-1 block font-semibold">Current password</label>
          <input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} required minLength={6} className="qb-input" data-testid="current-password-input" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-600 mb-1 block font-semibold">New password</label>
            <input type="password" value={next1} onChange={(e) => setNext1(e.target.value)} required minLength={6} className="qb-input" data-testid="new-password-input" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-600 mb-1 block font-semibold">Confirm new password</label>
            <input type="password" value={next2} onChange={(e) => setNext2(e.target.value)} required minLength={6} className="qb-input" data-testid="new-password-confirm-input" />
          </div>
        </div>
        {err && <div className="border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2 rounded-lg" data-testid="change-password-error">{err}</div>}
        {ok && <div className="border border-emerald-200 bg-emerald-50 text-emerald-700 text-sm px-3 py-2 rounded-lg inline-flex items-center gap-2" data-testid="change-password-ok"><Check size={14} /> Password updated.</div>}
        <button disabled={busy} type="submit" className="inline-flex items-center gap-2 h-10 px-5 bg-brand-blue text-white text-xs uppercase tracking-wider font-semibold hover:bg-brand-blue-dark disabled:opacity-60 rounded-full" data-testid="change-password-submit">
          <Save size={14} /> {busy ? "Updating…" : "Update password"}
        </button>
      </form>
    </div>
  );
}
